import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { searchAPI } from "../axios/search-axios";
import useFeedbackMonitor from "../custom-hooks/useFeedbackMonitor";
import { normalizeSearchRequest, getSubmittedSearchRequest, getHighlightSearchTerm } from "../utils/searchRequest";
import { canReuseSearchAnalytics, getTranslatedSearchTerm } from "../utils/searchResponse";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 10;
const MAX_PAGES_FROM_API = 100;

const getResultWindowStartPage = (page) =>
  Math.floor((Math.max(page, 1) - 1) / MAX_PAGES_FROM_API) *
    MAX_PAGES_FROM_API +
  1;

const useSearchResults = () => {
  const location = useLocation();
  const searchString = location.state ? location.state.searchString : "";
  const hasLocationEffectiveSearchTerm = Object.prototype.hasOwnProperty.call(
    location.state || {},
    "effectiveSearchTerm"
  );

  const [searchResults, setSearchResults] = useState(
    location.state?.searchResults || []
  );
  const [effectiveSearchTerm, setEffectiveSearchTerm] = useState(
    hasLocationEffectiveSearchTerm
      ? location.state.effectiveSearchTerm
      : (localStorage.getItem("effectiveSearchTerm")
        ? JSON.parse(localStorage.getItem("effectiveSearchTerm"))
        : searchString)
  );
  const submittedSearchRequest = useMemo(
    () => getSubmittedSearchRequest(location.state),
    [location.state]
  );
  const matchType = submittedSearchRequest.match_type;
  const [highlightSearchTerm, setHighlightSearchTerm] = useState(() =>
    getHighlightSearchTerm(submittedSearchRequest, effectiveSearchTerm)
  );

  const [searchResultSets, setSearchResultSets] = useState({});
  const [searchAfterSets, setSearchAfterSets] = useState({});
  const paginationRequestId = useRef(0);
  const analyticsRequestGeneration = useRef(0);
  const analyticsQueryKey = useRef(canReuseSearchAnalytics(
    location.state?.analytics, location.state?.analyticsQueryKey,
  ) ? location.state.analyticsQueryKey : null);

  const {
    showFeedbackPopup,
    handleCloseFeedbackPopup,
    fingerprint,
    isNewUser,
  } = useFeedbackMonitor();

  const DEFAULT_API_SORT_PARAM = "date";
  const DEFAULT_API_SORT_TYPE = "asc";

  const withDownloadBlockedFlag = (docs = [], blockedList = []) =>
    docs.map((doc) => ({
      ...doc,
      is_download_blocked: blockedList.includes(doc.coll_identifier),
    }));

  //Filter Modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const persistEffectiveSearchTerm = (response) => {
    const nextEffectiveSearchTerm = submittedSearchRequest.enable_translation
      ? getTranslatedSearchTerm(response)
      : "";
    setEffectiveSearchTerm(nextEffectiveSearchTerm);
    setHighlightSearchTerm(getHighlightSearchTerm(submittedSearchRequest, nextEffectiveSearchTerm));
    localStorage.setItem(
      "effectiveSearchTerm",
      JSON.stringify(nextEffectiveSearchTerm)
    );
  };

  const fetchResultWindow = async (
    page,
    searchAfterCursor,
    requestId
  ) => {
    const filters = JSON.parse(localStorage.getItem("filters"));
    let requestBody = {
      ...submittedSearchRequest,
      sort_param: activeSort.param,
      sort_type: activeSort.type,
    };
    if (searchAfterCursor) {
      requestBody.search_after = searchAfterCursor;
    }
    if (filters)
      requestBody = {
        ...filters,
        ...requestBody,
      };
    const response = await fetchSearchData(requestBody, requestId);
    if (requestId !== paginationRequestId.current) {
      throw new Error("Search changed while loading results");
    }
    const newSearchResults = withDownloadBlockedFlag(
      response.data.docs,
      response.data.download_blocked_list || []
    );
    persistEffectiveSearchTerm(response);

    if (typeof response.data.count === "number") {
      setTotalCount(response.data.count);
    }
    setSearchAfter(response.data.search_after);
    setSearchResultSets((prevSearchResultSets) => ({
      ...prevSearchResultSets,
      [page]: newSearchResults,
    }));
    setSearchAfterSets((prevSearchAfterSets) => ({
      ...prevSearchAfterSets,
      [page]: response.data.search_after,
    }));
    return { docs: newSearchResults, cursor: response.data.search_after };
  };

  useEffect(() => {}, [searchResultSets]);

  //Whenever location.state.searchResults changes, update the searchResultSets state
  useEffect(() => {
    paginationRequestId.current += 1;
    analyticsRequestGeneration.current += 1;
    setLoading(false);
    setCurrentPage(1);
    analyticsQueryKey.current = canReuseSearchAnalytics(
      location.state?.analytics, location.state?.analyticsQueryKey,
    ) ? location.state.analyticsQueryKey : null;
    setAnalytics(location.state?.analytics || {});
    if (location.state?.analytics != null) {
      localStorage.setItem("analytics", JSON.stringify(location.state.analytics));
    }
    if (location.state && location.state.searchResults) {
      setSearchResultSets((prevSearchResultSets) => ({
        1: location.state.searchResults,
      }));
    }

    if (typeof location.state?.totalCount === "number") {
      setTotalCount(location.state.totalCount);
    }

    setSearchAfter(location.state?.searchAfter || null);
    setSearchAfterSets({ 1: location.state?.searchAfter || null });

    setHighlightSearchTerm(getHighlightSearchTerm(
      submittedSearchRequest,
      location.state?.effectiveSearchTerm
    ));

    if (hasLocationEffectiveSearchTerm) {
      const nextEffectiveSearchTerm = location.state.effectiveSearchTerm || "";
      setEffectiveSearchTerm(nextEffectiveSearchTerm);
      localStorage.setItem(
        "effectiveSearchTerm",
        JSON.stringify(nextEffectiveSearchTerm)
      );
    }
  }, [hasLocationEffectiveSearchTerm, location.state, submittedSearchRequest]);

  //Headings sorting - whenever a user clicks a heading on the table, send existing filters / parameters plus "sort_param" (date, etc depending on what was clicked) and "sort_type" (asc/desc - asc on first click, desc on next, and so on) to the same /search endpoint. Take the new results, set them in searchResultSets, set the pagination page number to 1 and update the table with the new results.

  // Below state keeps track of sortParam( basically sort by column)
  const [sortParam, setSortParam] = useState(DEFAULT_API_SORT_PARAM);
  const [nextSortType, setNextSortType] = useState("desc");
  const [activeSort, setActiveSort] = useState({
    param: DEFAULT_API_SORT_PARAM,
    type: DEFAULT_API_SORT_TYPE,
  });

  const fetchSearchData = async (requestBody, requestId) => {
    const generation = ++analyticsRequestGeneration.current;
    const cachedKey = analyticsQueryKey.current;
    const response = await searchAPI.put("", {
      ...normalizeSearchRequest(requestBody),
      include_analytics: !cachedKey,
      ...(cachedKey ? { analytics_query_key: cachedKey } : {}),
    });
    if (generation !== analyticsRequestGeneration.current || requestId !== paginationRequestId.current) {
      throw new Error("Search changed while loading results");
    }
    // A changed query/index makes the backend return fresh analytics even
    // when reuse was requested. Null analytics preserve the current counts.
    if (response.data.analytics != null) {
      const nextAnalytics = response.data.analytics;
      analyticsQueryKey.current = canReuseSearchAnalytics(
        nextAnalytics, response.data.analytics_query_key,
      ) ? response.data.analytics_query_key : null;
      setAnalytics(nextAnalytics);
      localStorage.setItem("analytics", JSON.stringify(nextAnalytics));
      if (location.state) {
        location.state.analytics = nextAnalytics;
        location.state.analyticsQueryKey = response.data.analytics_query_key;
      }
    }
    return response;
  };

  const fetchSortedData = async (curSortType, curSortParam) => {
    const requestId = ++paginationRequestId.current;
    try {
      setLoading(true);
      const filters = JSON.parse(localStorage.getItem("filters"));
      let requestBody = {
        ...submittedSearchRequest,
        sort_param:
          curSortType === "neutral" ? DEFAULT_API_SORT_PARAM : curSortParam,
        //In neutral state the curSortType is always asc
        sort_type: curSortType === "neutral" ? DEFAULT_API_SORT_TYPE : curSortType,
      };
      if (curSortType !== "neutral") {
        requestBody.sort_type = curSortType;
      }
      // set filters
      if (filters)
        requestBody = {
          ...filters,
          ...requestBody,
        };
      const response = await fetchSearchData(requestBody, requestId);
      const newSearchResults = withDownloadBlockedFlag(
        response.data.docs,
        response.data.download_blocked_list || []
      );
      persistEffectiveSearchTerm(response);

      if (typeof response.data.count === "number") {
        setTotalCount(response.data.count);
      }
      setSearchAfter(response.data.search_after);
      setSearchAfterSets({
        1: response.data.search_after,
      });
      setActiveSort({
        param: curSortType === "neutral" ? DEFAULT_API_SORT_PARAM : curSortParam,
        type: curSortType === "neutral" ? DEFAULT_API_SORT_TYPE : curSortType,
      });
      setSearchResultSets((prevSearchResultSets) => ({
        1: newSearchResults,
      }));
      setSearchResults(newSearchResults);
      //Set location.state.searchResults to newSearchResults
      if (location.state) {
        location.state.searchResults = newSearchResults;
        location.state.searchAfter = response.data.search_after;
        location.state.totalCount = response.data.count;
      }
      //Set localStorage searchResults to newSearchResults
      localStorage.setItem("searchResults", JSON.stringify(newSearchResults));
      setCurrentPage(1);
      return true;
    } catch (error) {
      console.error("Error fetching additional results:", error);
      return false;
    } finally {
      if (requestId === paginationRequestId.current) setLoading(false);
    }
  };

  const handleHeadingClick = async (columnName) => {
    if (columnName === "relevance") {
      setSortParam("relevance");
      if (!await fetchSortedData("desc", "relevance")) return;
      setNextSortType("desc");
      return;
    }

    // neutral -> asc -> desc -> neutral -> asc -> desc -> neutral -> ...
    let newNextSortType;
    if (sortParam === columnName) {
      if (nextSortType === "asc") newNextSortType = "desc";
      else if (nextSortType === "desc") newNextSortType = "neutral";
      else newNextSortType = "asc";
      if (!await fetchSortedData(nextSortType, columnName)) return;
      if (nextSortType === "neutral") {
        setSortParam(DEFAULT_API_SORT_PARAM);
        newNextSortType = "desc";
      }
    } else {
      setSortParam(columnName);
      if (!await fetchSortedData("asc", columnName)) return;
      newNextSortType = "desc";
    }
    setNextSortType(newNextSortType);
  };

  const sortArrowElement = (col_type) => {
    if (col_type === "relevance") {
      return sortParam === "relevance" ? " ▼" : "";
    }
    // asc -> desc -> neutral -> asc -> desc -> neutral -> ...
    if (col_type === sortParam) {
      if (nextSortType === "asc") {
        // current neutral
        return " ▽△";
      } else if (nextSortType === "desc") {
        // current asc
        return " ▲";
      } else {
        // next neutral, current desc
        return " ▼";
      }
    } else {
      return " △▽";
    }
  };

  const [searchAfter, setSearchAfter] = useState(
    location.state?.searchAfter || ""
  );
  const [totalCount, setTotalCount] = useState(
    typeof location.state?.totalCount === "number"
      ? location.state.totalCount
      : (location.state?.searchResults || []).length
  );

  // Loading
  const [loading, setLoading] = useState(false);

  //Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [analytics, setAnalytics] = useState(location.state?.analytics || {});

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  //For current page
  let startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  let endIndex = startIndex + ITEMS_PER_PAGE;
  let displayedRows = [];
  let currentSearchResults;
  let hashMapKey = getResultWindowStartPage(currentPage);
  if (searchResultSets[hashMapKey] && currentPage >= hashMapKey) {
    currentSearchResults = searchResultSets[hashMapKey];
    localStorage.setItem("searchResults", JSON.stringify(currentSearchResults));
    startIndex = (currentPage - hashMapKey) * ITEMS_PER_PAGE;
    endIndex = startIndex + ITEMS_PER_PAGE;
    displayedRows = Array.isArray(currentSearchResults)
      ? currentSearchResults.slice(startIndex, endIndex)
      : [];
  }

  const emptyRowsCount = ITEMS_PER_PAGE - displayedRows.length;

  const loadResultWindowForPage = async (page, requestId) => {
    const targetWindowStart = getResultWindowStartPage(page);
    const targetOffset = (page - targetWindowStart) * ITEMS_PER_PAGE;
    if (searchResultSets[targetWindowStart]?.length > targetOffset) return;

    const loadedWindowStarts = Object.keys(searchResultSets)
      .map(Number)
      .filter((windowStart) => windowStart < targetWindowStart && searchAfterSets[windowStart])
      .sort((a, b) => b - a);
    const nearestLoadedWindowStart = loadedWindowStarts[0] || 1;
    let cursor = searchAfterSets[nearestLoadedWindowStart];

    // Searches opened before a backend update may have no cursor. Refresh the
    // starting batch once so a corrected backend can supply it.
    if (!cursor) {
      const refreshed = await fetchResultWindow(nearestLoadedWindowStart, null, requestId);
      cursor = refreshed.cursor;
      if (targetWindowStart === 1 && refreshed.docs.length > targetOffset) return;
    }

    if (!cursor) throw new Error("No cursor available for the requested page");

    for (
      let windowStart = nearestLoadedWindowStart + MAX_PAGES_FROM_API;
      windowStart <= targetWindowStart;
      windowStart += MAX_PAGES_FROM_API
    ) {
      const batch = await fetchResultWindow(windowStart, cursor, requestId);
      if (windowStart === targetWindowStart && batch.docs.length > targetOffset) return;
      cursor = batch.cursor;
      if (!cursor || batch.docs.length === 0) break;
    }
    throw new Error("The requested results page could not be loaded");
  };

  const handlePageChange = async (page) => {
    if (!Number.isInteger(page) || page < 1 || page > totalPages) return;
    const requestId = ++paginationRequestId.current;
    setLoading(true);
    try {
      await loadResultWindowForPage(page, requestId);
      if (requestId === paginationRequestId.current) setCurrentPage(page);
    } catch (error) {
      if (requestId === paginationRequestId.current) {
        toast.error(localStorage.getItem("lang") === "ar"
          ? "تعذر تحميل صفحة النتائج. يرجى المحاولة مرة أخرى."
          : "Could not load this results page. Please try again.");
      }
    } finally {
      if (requestId === paginationRequestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setSearchResultSets((prevSearchResultSets) => ({
      1: searchResults || location.state.searchResults,
    }));
    if (typeof location.state?.totalCount === "number") {
      setTotalCount(location.state.totalCount);
    }
    if (location.state?.searchAfter) {
      setSearchAfter(location.state.searchAfter);
      setSearchAfterSets({
        1: location.state.searchAfter,
      });
    }
  }, []);

  //PDF Modal

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  //Analytics modal
  const [analyticsModalOpen, setAnalyticsModalOpen] = useState(false);

  // For making clicked rows purple
  const [visitedRows, setVisitedRows] = useState(() => {
    const storedVisitedRows =
      JSON.parse(localStorage.getItem("visitedRows")) || [];
    return new Set(storedVisitedRows);
  });

  // To make visited rows persist across sessions

  useEffect(() => {
    const storedVisitedRows =
      JSON.parse(localStorage.getItem("visitedRows")) || [];
    setVisitedRows(new Set(storedVisitedRows));
  }, []);

  useEffect(() => {
    localStorage.setItem("visitedRows", JSON.stringify([...visitedRows]));
  }, [visitedRows]);

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setModalOpen(true);
    setVisitedRows((prev) => {
      const newSet = new Set(prev);
      newSet.add(row.AWS_link);
      localStorage.setItem("visitedRows", JSON.stringify([...newSet]));
      return newSet;
    });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleCloseAnalyticsModal = () => {
    setAnalyticsModalOpen(false);
  };

  useEffect(() => {}, [analytics, searchResults]);

  //Loop through searchResults, count the number of each collection type, and store in an array of objects
  const collTypeCounts = [];
  const storedResults = JSON.parse(localStorage.getItem("searchResults"));

  // const updatedResults = searchResults;
  let updatedResults = JSON.parse(localStorage.getItem("analytics"));
  updatedResults = analytics;

  //! Format

  if (
    updatedResults &&
    updatedResults.coll_type &&
    updatedResults.coll_type.buckets
  ) {
    let formatResults = updatedResults.coll_type.buckets;
    formatResults.forEach((result) => {
      const collType = result.key;
      const count = result.doc_count;
      collTypeCounts.push({ coll_type: collType, count: count });
    });
  }

  const lang = localStorage.getItem("lang");
  const isArabic = lang === "ar";

  //! Collection
  const collNameIdentifier = [];
  const collectionKey = isArabic
    ? "analytics_per_collection_arabic"
    : "analytics_per_collection";
  if (
    updatedResults &&
    updatedResults[collectionKey] &&
    updatedResults[collectionKey].buckets
  ) {
    updatedResults[collectionKey].buckets.forEach((result) => {
      const collName = result.key;
      const count = result.doc_count;
      collNameIdentifier.push({
        [isArabic ? "coll_name_arabic" : "coll_name"]: collName,
        count,
      });
    });
  }

  //! Source
  const collSourceCounts = [];
  const sourceKey = isArabic ? "coll_source_arabic" : "coll_source";
  if (
    updatedResults &&
    updatedResults[sourceKey] &&
    updatedResults[sourceKey].buckets
  ) {
    updatedResults[sourceKey].buckets.forEach((result) => {
      const collSource = result.key;
      const count = result.doc_count;
      collSourceCounts.push({
        [isArabic ? "coll_source_arabic" : "coll_source"]: collSource,
        count,
      });
    });
  }

  //! Location
  const collLocationCounts = [];
  const locationKey = isArabic ? "coll_location_arabic" : "coll_location";
  if (
    updatedResults &&
    updatedResults[locationKey] &&
    updatedResults[locationKey].buckets
  ) {
    updatedResults[locationKey].buckets.forEach((result) => {
      const collLocation = result.key;
      const count = result.doc_count;
      collLocationCounts.push({
        [isArabic ? "coll_location_arabic" : "coll_location"]: collLocation,
        count,
      });
    });
  }

  // Keep the cache flag in sync when the page is left without triggering a
  // browser confirmation dialog.
  useEffect(() => {
    const handlePageHide = () => {
      localStorage.setItem("searchItemsSet", false);
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  useEffect(() => {
    if (localStorage.getItem("searchItemsSet") === "false") {
      localStorage.setItem("searchResults", JSON.stringify(searchResults));
      localStorage.setItem("searchItemsSet", true);
    }
  }, [searchResults]);

  useEffect(() => {}, [storedResults, searchResults, updatedResults]);

  const handleAnalyticsClick = () => {
    setAnalyticsModalOpen(true);
  };

  return {
    searchResults,
    setSearchResults,
    searchResultSets,
    setSearchResultSets,
    handleHeadingClick,
    sortArrowElement,
    handlePageChange,
    currentPage,
    totalPages,
    displayedRows,
    emptyRowsCount,
    handleRowClick,
    modalOpen,
    selectedRow,
    handleCloseModal,
    handleAnalyticsClick,
    analyticsModalOpen,
    handleCloseAnalyticsModal,
    collTypeCounts,
    collNameIdentifier,
    collSourceCounts,
    collLocationCounts,
    handleShow,
    show,
    handleClose,
    searchAfter,
    setSearchAfter,
    setTotalCount,
    setSortParam,
    setNextSortType,
    setActiveSort,
    loading,
    setLoading,
    fingerprint,
    isNewUser,
    setShow,
    setAnalyticsModalOpen,
    setAnalytics,
    setCurrentPage,
    searchString,
    visitedRows,
    effectiveSearchTerm,
    highlightSearchTerm,
    setEffectiveSearchTerm,
    matchType,
  };
};

export default useSearchResults;
